<script setup lang="ts">import { computed as _computed, toRef as _toRef } from 'vue';

import {computed, onMounted, watchEffect} from 'vue'
import AppContent from '@toolify/client/src/modules/app/components/AppContent.vue'
import NativeWindowHeaderComponent
  from '@toolify/client/src/modules/electron/components/NativeWindowHeaderComponent.vue'
import {AppPlatformType} from '@toolify/server/src/models/mongoose/SessionModel/enum/AppPlatformType'
import {useAuthStore} from '@toolify/client/src/stores/AuthStore/useAuthStore'

import {useCssVar} from '@vueuse/core'
import {useHead} from '@unhead/vue'
import {useProvideDropdown} from '@toolify/client/src/modules/modals/composables/useDropdown'
import {useProvideOverlayStack} from '@toolify/client/src/modules/modals/composables/useOverlayStack'
import {useCurrentAppPlatform} from '@toolify/client/src/modules/platform/composables/useCurrentAppPlatform'
import {useKeyboardSafeArea} from '../composables/useKeyboardSafeArea'
import {TextZoom} from '@capacitor/text-zoom'
import {ref} from 'vue'
import {useThemeVariables} from '@toolify/client/src/modules/theme/composables/useThemeVariables'
import {ThemeVariableType} from '@toolify/client/src/modules/theme/enum/ThemeVariableType'
import {useResponsive} from '../composables/useResponsive'
import {useInternalLinkInterception} from '../composables/useInternalLinkInterception'
import {useWorkspaceTabMiddleClick} from '@toolify/client/src/modules/workspaceTabs/composables/useWorkspaceTabMiddleClick'
import IncomingCallOverlayContainer from '@toolify/client/src/modules/call/containers/IncomingCallOverlayContainer.vue'
import ScreenSharePickerContainer from '@toolify/client/src/modules/call/containers/ScreenSharePickerContainer.vue'
import {useKeyboardShortcuts} from '@toolify/client/src/modules/keyboard/composables/useKeyboardShortcuts'
import {KeyboardModifierKeyTypes} from '@toolify/client/src/stores/KeyboardShortcutStore/enum/KeyboardModifierKeyTypes'
import {useAppLayoutStore} from '@toolify/client/src/stores/AppLayoutStore/useAppLayoutStore'
import GlobalSearchOverlay from '@toolify/client/src/modules/home/components/GlobalSearchOverlay.vue'
import {useNativeAppStore} from '@toolify/client/src/stores/AppTitlebarStore/useNativeAppStore'
import {EntityType} from '@toolify/server/src/services/EntityService/enum/EntityType'
import {IUserResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IUserResponseInstance'
import {useSystemMessageStore} from '@toolify/client/src/stores/SystemMessageStore/useSystemMessageStore'
import SystemMessageBannerComponent from '../modules/layout/components/SystemMessageBannerComponent.vue'
import {useReferenceDataCacheInvalidation} from '@toolify/client/src/composables/useReferenceDataCacheInvalidation'
import {useGuestAppearance} from '@toolify/client/src/modules/authentication/composables/useGuestAppearance'
import {resolveThemeType} from '@toolify/server/src/services/UserSettingsService/strategies/ThemeUserSettingStrategy/functions/resolveThemeType'
import {i18n} from '@toolify/client/src/setup/i18n'

const {currentAppPlatformType, isAppPlatformMobile} = useCurrentAppPlatform()
const hasNativeHeader = currentAppPlatformType === AppPlatformType.Desktop
const nativeAppStore = useNativeAppStore()
const appLayoutStore = useAppLayoutStore()
const authStore = useAuthStore()
const systemMessagesStore = useSystemMessageStore()

useReferenceDataCacheInvalidation()
const {theme: guestTheme} = useGuestAppearance()

const currentUser = _computed(() => {
  if (authStore.currentEntity?.entityType === EntityType.User) {
    return authStore.currentEntity.payload as IUserResponseInstance
  }
  return null
})

const shouldEnableDevTools = _computed(() => {
  return !import.meta.env.PROD || !!currentUser.value?.isSuperAdmin
})

watchEffect(() => {
  if (!hasNativeHeader || !window.electronAPI?.setDevToolsEnabled) {
    return
  }
  window.electronAPI.setDevToolsEnabled(shouldEnableDevTools.value)
})

useKeyboardShortcuts([
  [[KeyboardModifierKeyTypes.Control, 'f'], () => {
    appLayoutStore.toggleSearchOverlay()
  }],
], () => {
  return !!authStore.currentEntity
})
useKeyboardShortcuts([
  [['F11'], () => {
    nativeAppStore.toggleAppFullscreen()
  }],
], () => {
  return currentAppPlatformType === AppPlatformType.Desktop
})

const currentTheme = _computed(() => {
  if(currentUser.value) {
    return resolveThemeType(currentUser.value.settings?.theme)
  }
  return guestTheme.value
})
const appBrandingName = ENV.AppBrandingName

const faviconHref = _computed(() => {
  const path = ENV.FaviconPath || 'favicon.ico'
  if (path.startsWith('http') || path.startsWith('/')) {
    return path
  }
  return `/${path}`
})

useHead({
  title: `${appBrandingName} - Most flexible CRM in the world`,
  htmlAttrs: {lang: computed(() => i18n.global.locale.value)},
  link: [
    {
      key: 'favicon',
      rel: 'icon',
      type: 'image/x-icon',
      href: faviconHref.value,
    },
  ],
})
useProvideDropdown()
useProvideOverlayStack()
const {isKeyboardVisible, isBottomPanelVisible, isEmojiPanelOpen, emojiPanelHeight, bottomWithKeyboard, top, right, left, bottom, visibleKeyboardHeight} = useKeyboardSafeArea()

useInternalLinkInterception()
useWorkspaceTabMiddleClick()
watchEffect(() => {
  document.documentElement.setAttribute('data-theme', currentTheme.value)
})

const preferredFontSizeFactor = ref(1)

const __$temp_1 = (useThemeVariables()),
  getThemeVariable = _toRef(__$temp_1, 'getThemeVariable');
const {isSmallerThanLarge} = useResponsive()

const mobileFontSizeFactor = _computed<number>(() => {
  return getThemeVariable.value(ThemeVariableType.MobileFontSizeFactor) as number
})

const fontSizeFactor = _computed(() => {
  const baseFactor = preferredFontSizeFactor.value
  if (isSmallerThanLarge.value) {
    return baseFactor * mobileFontSizeFactor.value
  }
  return baseFactor
})

const appFontSizeFactor = useCssVar('--app-font-size-factor', document.documentElement)

watchEffect(() => {
  appFontSizeFactor.value = String(fontSizeFactor.value)
})

onMounted(async () => {
  if(!isAppPlatformMobile) {
    return
  }
  const preferred = await TextZoom.getPreferred()
  preferredFontSizeFactor.value = preferred.value
})


const safeAreaValuesWithPixels = _computed(() => {
  return {
    bottomWithKeyboard: `${bottomWithKeyboard.value}px`,
    bottom: `${bottom.value}px`,
    top: `${top.value}px`,
    right: `${right.value}px`,
    left: `${left.value}px`,
    visibleKeyboardHeight: `${visibleKeyboardHeight.value}px`,
    isKeyboardVisibleFactor: isBottomPanelVisible.value ? 0 : 1,
    emojiPanelOffset: isEmojiPanelOpen.value ? `${emojiPanelHeight.value}px` : '0px',
  }
})

const appContentGridRows = computed(() => {
  const rows: string[] = []
  if(systemMessagesStore.hasVisibleSystemMessage) {
    rows.push('auto')
  }
  if(hasNativeHeader) {
    rows.push('auto')
  }
  rows.push('1fr')
  return rows.join(' ')
})
</script>

<template>
  <div
    class="app-wrapper"
    :class="{
      'has-native-header': hasNativeHeader,
      'is-native-fullscreen': hasNativeHeader && nativeAppStore.isWindowInFullscreen,
      'is-mobile-keyboard-visible': isKeyboardVisible,
      'is-system-message-visible': systemMessagesStore.hasVisibleSystemMessage,
    }"
    :data-theme="currentTheme"
    :data-platform-type="currentAppPlatformType"
  >
    <div class="floating-container" id="tooltip-divs" data-overlay-ignore/>
    <div class="floating-container" id="dropdown-divs"/>
    <div class="teleported-container" id="teleported"/>
    <NativeWindowHeaderComponent v-if="hasNativeHeader"/>
    <SystemMessageBannerComponent v-if="systemMessagesStore.hasVisibleSystemMessage"/>
    <IncomingCallOverlayContainer/>
    <ScreenSharePickerContainer/>
    <AppContent/>
    <GlobalSearchOverlay
      :hasNativeHeader="hasNativeHeader && !nativeAppStore.isWindowInFullscreen"
    />
  </div>
</template>

  <style scoped lang="scss">
  @use '../styles/variables' as variables;

  .app-wrapper {
    width: 100%;
    height: 100%;
    color: map-get(variables.$colors, 'content-normal');
    background: map-get(variables.$colors, surface-dim);
    box-sizing: border-box;
    overflow: hidden;
    overscroll-behavior: none;
    display: grid;
    grid-template-rows: v-bind('appContentGridRows');
    --safe-area-bottom-with-keyboard: calc(v-bind('safeAreaValuesWithPixels.visibleKeyboardHeight') + env(safe-area-inset-bottom) * v-bind('safeAreaValuesWithPixels.isKeyboardVisibleFactor'));
    --safe-area-bottom: env(safe-area-inset-bottom);
    --safe-area-top: env(safe-area-inset-top);
    --content-safe-area-top: env(safe-area-inset-top);
    --safe-area-right: env(safe-area-inset-right);
    --safe-area-left: env(safe-area-inset-left);
    --native-window-header-offset: 0px;

    &.has-native-header:not(.is-native-fullscreen) {
      --native-window-header-offset: 2.5rem;
    }

    &.is-system-message-visible {
      --content-safe-area-top: 0px;
    }
  }

  .floating-container {
    position: absolute;
    left: 20px;
    top: 20px;
    width: calc(100% - 40px);
    height: calc(100% - 40px);
    overflow: hidden;

    .app-wrapper.has-native-header:not(.is-native-fullscreen) & {
      top: calc(20px + 32px);
      height: calc(100% - 40px - 32px);
    }
  }

  .teleported-container {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;

    .app-wrapper.has-native-header:not(.is-native-fullscreen) & {
      top: 32px;
      height: calc(100% - 32px);
    }
  }
</style>

